/**
 * AI 리뷰 콘텐츠 변환 유틸리티
 * 뷰어와 에디터 간의 데이터 형식 일관성을 보장합니다.
 */
import { demaskHospitalReferences, HospitalAliasMap } from "./extractDepartment";

interface TransformLog {
  timestamp: string;
  direction: 'toMarkdown' | 'fromMarkdown';
  context: string;
  input: string;
  output: string;
  duration: number;
  inputLength: number;
  outputLength: number;
  transformations: Array<{
    type: string;
    count: number;
  }>;
}

interface ReviewContentTransform {
  enableLogging: boolean;
  transformLogs: TransformLog[];
  toMarkdown: (text: string, context?: string, referenceMode?: 'button' | 'none' | 'include', hospitalAliasMap?: HospitalAliasMap) => string;
  getTransformHistory: () => TransformLog[];
  clearLogs: () => void;
}

export const reviewContentTransform: ReviewContentTransform = {
  // 로그 활성화 플래그 (개발 환경에서만)
  enableLogging: process.env.NODE_ENV === 'development',
  
  // 변환 로그 저장소
  transformLogs: [],
  
  /**
   * 원본 텍스트를 마크다운으로 변환
   * ReviewSector의 referenceMarkdownPlugin 로직을 포함
   */
  toMarkdown: (text: string, context?: string, referenceMode: 'button' | 'none' | 'include' = 'button', hospitalAliasMap?: HospitalAliasMap): string => {
    const startTime = performance.now();
    const inputSnapshot = text.substring(0, 200); // 처음 200자만

    try {
      if (!text) return '';

      let result = text;

      // [병원 변환 · 필수 선행 · spec 설계 결정 4] ref 변환보다 먼저 실행한다.
      // ref 정규식 /⑆(?!img_)([A-Za-z0-9_]+)/이 맨몸 ⑆A를 가짜 ref로 잡으면 이후 인용 번호가
      // 통째로 밀리므로, 그 리뷰의 hospitalAlias 집합(맵 키)을 화이트리스트로 먼저 복원한다.
      // 'none'(임베드 등)은 실명 평문, 그 외는 [실명](#hospital-A) 중간형으로 복원한다.
      if (hospitalAliasMap && Object.keys(hospitalAliasMap).length > 0) {
        result = demaskHospitalReferences(result, hospitalAliasMap, referenceMode === 'none' ? 'plain' : 'link');
      }

      // 참고문헌 ID와 번호 매핑을 위한 Map
      const referenceMap = new Map<string, number>();
      let referenceCounter = 1;

      // referenceMode가 'none'일 때 특별 처리
      if (referenceMode === 'none') {
        // 수평선 보호(Phase 2 · 계획 §3-가): 한 줄 전체가 수평선(-{3,})인 줄만 센티넬로 치환해,
        // 아래 구분자 정리(특히 `([,\-])\s*[,\-]+` → '$1')가 수평선을 하이픈 1개로 붕괴시키고
        // 빈 리스트 항목(불릿)으로 렌더되던 결함을 막는다. 정규식은 `[ \t]`로 좁힌다 —
        // `\s`는 /gm 멀티라인에서 개행을 삼켜 수평선 앞 빈 줄을 흡수(단락 병합)한다.
        const HR_SENTINEL = "HR";
        result = result.replace(/^[ \t]*-{3,}[ \t]*$/gm, HR_SENTINEL);

        // 참고문헌 패턴 제거 (⑆img_ 약품 이미지 토큰은 제외 — 'button' 분기와 동일한 (?!img_) 예외).
        // 이 예외가 없으면 ⑆img_ 토큰이 참고문헌으로 오인돼 삭제되고, MarkdownViewer.processTextNode가
        // DrugImage로 렌더하기 전에 사라진다(커밋 0631130이 'button' 분기에만 넣고 'none'은 빠뜨린 결함).
        result = result.replace(/#([A-Za-z0-9]+)/g, '');
        result = result.replace(/⑆(?!img_)([A-Za-z0-9_]+)/g, '');
        
        // 중괄호 내용 정리
        // 빈 중괄호 {} 제거
        result = result.replace(/\{\s*\}/g, '');
        // 구분자만 남은 경우: {, , ,} 또는 {- - -} -> 제거
        result = result.replace(/\{\s*(?:[,\-]\s*)+\}/g, '');
        // 앞쪽 구분자 정리: {, something} -> {something}
        result = result.replace(/\{\s*[,\-]+\s*/g, '{');
        // 뒤쪽 구분자 정리: {something, } -> {something}
        result = result.replace(/\s*[,\-]+\s*\}/g, '}');
        // 중간 연속 구분자 정리: ,, -> , 또는 -- -> -
        result = result.replace(/([,\-])\s*[,\-]+/g, '$1');
        // 최종 빈 중괄호 제거
        result = result.replace(/\{\s*\}/g, '');
        
        // 소괄호 내용 정리 (기존 로직과 동일)
        // 빈 괄호 () 제거
        result = result.replace(/\(\s*\)/g, '');
        // 구분자만 남은 경우 처리: (, , ,) -> 제거
        result = result.replace(/\(\s*(?:[,\-]\s*)+\)/g, '');
        // 앞쪽 구분자 정리: (, something) -> (something)
        result = result.replace(/\(\s*[,\-]+\s*/g, '(');
        // 뒤쪽 구분자 정리: (something, ) -> (something)
        result = result.replace(/\s*[,\-]+\s*\)/g, ')');
        // 최종 빈 괄호 제거
        result = result.replace(/\(\s*\)/g, '');

        // 수평선 복원(Phase 2): 센티넬 → 정본 마크다운 수평선(---). 이후 공유 변환(`─{3,}`·`^-{10,}$`)에
        // 걸리지 않아 그대로 <hr>로 렌더된다. 3단(button 모드)이 `-----`를 <hr>로 렌더하는 것과 시각적으로 동일.
        result = result.split(HR_SENTINEL).join("---");
      } else {
        // 기존 로직: 참고문헌을 마크다운 링크로 변환
        // 모든 참고문헌 ID를 먼저 찾아서 번호 할당 (img_ 제외).
        //  - 스캔 대상을 text가 아닌 병원 변환 후 result로 둔다 — 맨몸 ⑆A가 이미 병원명으로
        //    복원돼 ref로 세지 않으므로 인용 번호가 밀리지 않는다.
        //  - `#(?!hospital-)`: 병원 중간형 [실명](#hospital-A)의 `#hospital`을 참고문헌으로
        //    오인해 `#reference-hospital`로 훼손하는 것을 막는다.
        const allReferences = [
          ...Array.from(result.matchAll(/#(?!hospital-)([A-Za-z0-9]+)/g)),
          ...Array.from(result.matchAll(/⑆(?!img_)([A-Za-z0-9_]+)/g)),
        ];

        // 순서대로 번호 할당 (중복 ID는 같은 번호 사용)
        allReferences.forEach((match) => {
          const refId = match[1];
          if (!referenceMap.has(refId)) {
            referenceMap.set(refId, referenceCounter++);
          }
        });

        // 기존 #참고문헌ID 패턴 지원 (하위 호환성 · #hospital 제외)
        result = result.replace(/#(?!hospital-)([A-Za-z0-9]+)/g, (match, refId) => {
          const number = referenceMap.get(refId) || 1;
          return `[ref${number}](#reference-${refId})`;
        });

        // 새로운 ⑆참고문헌ID 패턴 지원 (소문자, 언더스코어 포함, img_ 제외)
        result = result.replace(/⑆(?!img_)([A-Za-z0-9_]+)/g, (match, refId) => {
          const number = referenceMap.get(refId) || 1;
          return `[ref${number}](#reference-${refId})`;
        });
      }

      // [병원 정리 패스 · spec 설계 결정 4] 두 변환 후에도 남은 ⑆+한글 기관명사 마커(매핑 실패·
      // 무맵 경로)는 ⑆만 벗겨 평문화한다 — 생 ⑆가 화면에 뜨는 것을 막는다.
      // ⑆상담필요·⑆근거확인필요·⑆img_은 기관명사/영문+_ 구조상 매칭되지 않아 보존된다.
      result = result.replace(/⑆(\s*(?:의료기관|의원|병원|기관))/g, '$1');
      result = result.replace(/⑆(\s*[A-Za-z]{1,2}\s*(?:의료기관|의원|병원|기관))/g, '$1');

      // 긴 유니코드 하이픈(─)을 마크다운 수평선으로 변환
      result = result.replace(/─{3,}/g, '---');

      // 일반 하이픈으로 된 긴 구분선도 마크다운 수평선으로 변환
      result = result.replace(/^-{10,}$/gm, '---');

      // 불릿 포인트(• )를 마크다운 리스트 문법(- )으로 변환
      result = result.replace(/^• /gm, '- ');
      result = result.replace(/^(\s+)• /gm, '$1- ');
      
      // <badge> 태그 처리 - 마크다운 문법 보존
      // 뒤에 빈 줄(\n\n)을 붙여 후속 텍스트가 같은 단락에 합쳐지지 않도록 함
      // 주의: DRP/blockquote 변환보다 먼저 수행해야 함
      //  - 분석결과 요약의 <badge>는 {DRP} 내부에 위치하므로, DRP가 나중에 실행되면서
      //    빈 줄도 '>'로 prefix 처리하여 blockquote 연속성이 유지됨
      result = result.replace(/<badge>(.*?)<\/badge>\s*/g, (match, content) => {
        return `<badge>${content.trim()}</badge>\n\n`;
      });

      // DRP 태그로 감싸진 내용을 처리
      result = result.replace(/\{DRP\}([\s\S]*?)\{\/DRP\}/g, (match, content) => {
        const quotedContent = content
          .split('\n')
          .map(line => line.trim() ? `> ${line}` : '>')
          .join('\n');
        return quotedContent;
      });

      // 매칭되지 않은 단독 DRP 태그 제거 (AI가 닫는 태그를 빠뜨린 경우 방어)
      result = result.replace(/\{DRP\}/g, '');
      result = result.replace(/\{\/DRP\}/g, '');

      // HTML 태그 처리 - blockquote 태그 내부 마크다운 처리
      // <blockquote> 태그 처리 - 마크다운 문법 보존
      result = result.replace(/<blockquote>\s*\n?([\s\S]*?)<\/blockquote>/g, (match, content) => {
        // blockquote 내부의 내용을 줄바꿈으로 시작하도록 보장
        return `<blockquote>\n\n${content.trim()}\n\n</blockquote>`;
      });
      
      if (reviewContentTransform.enableLogging) {
        const log: TransformLog = {
          timestamp: new Date().toISOString(),
          direction: 'toMarkdown',
          context: context || 'unknown',
          input: inputSnapshot,
          output: result.substring(0, 200),
          duration: performance.now() - startTime,
          inputLength: text.length,
          outputLength: result.length,
          transformations: [
            { type: 'reference_links', count: (text.match(/#[A-Za-z0-9]+/g) || []).length },
            { type: 'new_reference_links', count: (text.match(/⑆[A-Za-z0-9_]+/g) || []).length },
            { type: 'drp_sections', count: (text.match(/\{DRP\}/g) || []).length / 2 },
            { type: 'bullet_points', count: (text.match(/^• /gm) || []).length },
          ]
        };
        
        reviewContentTransform.transformLogs.push(log);
        console.log('[Transform Log]', log);
      }
      
      return result;
    } catch (error) {
      console.error('[Transform Error]', {
        direction: 'toMarkdown',
        error: error instanceof Error ? error.message : String(error),
        input: inputSnapshot
      });
      return text; // 실패 시 원본 반환
    }
  },
  
  // 로그 조회 헬퍼
  getTransformHistory: () => reviewContentTransform.transformLogs,

  clearLogs: () => {
    reviewContentTransform.transformLogs = [];
  }
};

// 개발자 콘솔에서 접근 가능하도록
if (process.env.NODE_ENV === 'development') {
  (window as any).__reviewTransformDebug = {
    getLogs: reviewContentTransform.getTransformHistory,
    clearLogs: reviewContentTransform.clearLogs,
    enable: () => { reviewContentTransform.enableLogging = true; },
    disable: () => { reviewContentTransform.enableLogging = false; }
  };
}

export default reviewContentTransform;